/**
 * src/lib/i18n.ts
 * ----------------
 * Customer widget internationalisation — English, French, Arabic with full
 * RTL/LTR. The agent already replies in the customer's language; this covers
 * the UI chrome (labels, buttons, placeholders) and the layout direction.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

const en = {
  'greeting.morning': 'Good morning', 'greeting.afternoon': 'Good afternoon', 'greeting.evening': 'Good evening',
  'hero.help': 'How can I help?',
  'hero.disclaimer': '{{org}} can make mistakes. Verify important information.',
  'chat.placeholder': 'Ask a question…',
  'chat.placeholderAuth': 'Ask about your account, orders, invoices…',
  'chat.thinking': 'Thinking',
  'chat.send': 'send',
  'chat.online': '{{org}} assistant · online',
  'chat.signedOut': 'Signed out',
  'chat.latest': 'Latest',
  'chat.sources': 'Sources',
  'chat.helpful': 'Helpful', 'chat.notHelpful': 'Not helpful', 'chat.copy': 'Copy',
  'chat.footer': '{{org}} customer support',
  'banner.anon': "You're chatting anonymously.", 'banner.anonBody': 'Account-specific actions are disabled.',
  'banner.signInUnlock': 'Sign in to unlock',
  'sidebar.newConversation': 'New conversation',
  'sidebar.recent': 'Recent', 'sidebar.signedOut': 'Signed out', 'sidebar.syncing': 'Syncing…',
  'sidebar.noConversations': 'No conversations yet.',
  'sidebar.anonTitle': 'Anonymous chat',
  'sidebar.anonBody': 'Your conversation stays on this device.',
  'sidebar.signInTo': 'Sign in', 'sidebar.signInRest': 'to save history and unlock account actions.',
  'sidebar.collapse': 'Collapse', 'sidebar.expand': 'Expand', 'sidebar.menu': 'Menu',
  'sidebar.untitled': 'Untitled conversation',
  'theme.light': 'Light', 'theme.dark': 'Dark',
  'auth.signIn': 'Sign in', 'auth.signOut': 'Sign out',
  'composer.attach': 'Attach file', 'composer.image': 'Insert image', 'composer.voiceInput': 'Voice input',
  'composer.liveVoice': 'Live voice', 'composer.liveVoiceCall': 'Live voice call', 'composer.send': 'Send',
  'voice.connecting': 'Connecting…',
  'voice.listeningHint': 'Listening — speak whenever you’re ready',
  'voice.thinking': 'Thinking…',
  'voice.live': 'Live voice', 'voice.transcript': 'Live transcript',
  'voice.transcriptEmpty': 'Conversation will appear here as you speak.',
  'voice.you': 'You', 'voice.mute': 'Mute', 'voice.unmute': 'Unmute',
  'voice.switchText': 'Switch to text', 'voice.endCall': 'End call',
  'voice.subtitlesShow': 'Show subtitles', 'voice.subtitlesHide': 'Hide subtitles',
  'voice.agentVoice': 'Agent voice', 'voice.appliesNext': 'Applies from the next reply.',
  'v2t.input': 'Voice input', 'v2t.listening': 'Listening…', 'v2t.transcribing': 'Transcribing…',
  'v2t.review': 'Review', 'v2t.microphone': 'Microphone',
  'v2t.transcribingBody': 'Transcribing your audio…',
  'v2t.stopTranscribe': 'Stop & transcribe', 'v2t.placeholder': 'Edit transcript before sending…',
  'v2t.willAppear': 'Your words will appear here as you speak.',
  'v2t.listeningCount': 'Listening… {{s}}s remaining. Your words will appear here.',
  'v2t.errorMic': 'Microphone permission denied. Please allow access and try again.',
  'v2t.errorTranscribe': 'Transcription failed. Please try again.',
  'common.cancel': 'Cancel', 'common.send': 'Send', 'common.close': 'Close',
  'sug.anon.0': 'What plans do you offer?',
  'sug.anon.1': 'What countries do you ship to?',
  'sug.anon.2': 'How does the free trial work?',
  'sug.anon.3': 'Where can I read your privacy policy?',
  'sug.anon.4': 'Update my billing address',
  'sug.anon.5': 'Reset my API key',
  'sug.auth.0': 'Update my billing address',
  'sug.auth.1': 'Rotate my production API key',
  'sug.auth.2': 'Resend last invoice as PDF',
  'sug.auth.3': 'Add a new teammate to my workspace',
}

const fr: typeof en = {
  'greeting.morning': 'Bonjour', 'greeting.afternoon': 'Bon après-midi', 'greeting.evening': 'Bonsoir',
  'hero.help': 'Comment puis-je vous aider ?',
  'hero.disclaimer': '{{org}} peut faire des erreurs. Vérifiez les informations importantes.',
  'chat.placeholder': 'Posez une question…',
  'chat.placeholderAuth': 'Questions sur votre compte, commandes, factures…',
  'chat.thinking': 'Réflexion',
  'chat.send': 'envoyer',
  'chat.online': 'Assistant {{org}} · en ligne',
  'chat.signedOut': 'Non connecté',
  'chat.latest': 'Plus récent',
  'chat.sources': 'Sources',
  'chat.helpful': 'Utile', 'chat.notHelpful': 'Pas utile', 'chat.copy': 'Copier',
  'chat.footer': 'Support client {{org}}',
  'banner.anon': 'Vous discutez de façon anonyme.', 'banner.anonBody': 'Les actions liées au compte sont désactivées.',
  'banner.signInUnlock': 'Connectez-vous pour débloquer',
  'sidebar.newConversation': 'Nouvelle conversation',
  'sidebar.recent': 'Récent', 'sidebar.signedOut': 'Non connecté', 'sidebar.syncing': 'Synchronisation…',
  'sidebar.noConversations': 'Aucune conversation pour le moment.',
  'sidebar.anonTitle': 'Discussion anonyme',
  'sidebar.anonBody': 'Votre conversation reste sur cet appareil.',
  'sidebar.signInTo': 'Connectez-vous', 'sidebar.signInRest': "pour enregistrer l'historique et débloquer les actions du compte.",
  'sidebar.collapse': 'Réduire', 'sidebar.expand': 'Agrandir', 'sidebar.menu': 'Menu',
  'sidebar.untitled': 'Conversation sans titre',
  'theme.light': 'Clair', 'theme.dark': 'Sombre',
  'auth.signIn': 'Se connecter', 'auth.signOut': 'Se déconnecter',
  'composer.attach': 'Joindre un fichier', 'composer.image': 'Insérer une image', 'composer.voiceInput': 'Saisie vocale',
  'composer.liveVoice': 'Voix en direct', 'composer.liveVoiceCall': 'Appel vocal en direct', 'composer.send': 'Envoyer',
  'voice.connecting': 'Connexion…',
  'voice.listeningHint': 'À l’écoute — parlez quand vous êtes prêt',
  'voice.thinking': 'Réflexion…',
  'voice.live': 'Voix en direct', 'voice.transcript': 'Transcription en direct',
  'voice.transcriptEmpty': 'La conversation apparaîtra ici pendant que vous parlez.',
  'voice.you': 'Vous', 'voice.mute': 'Couper le micro', 'voice.unmute': 'Réactiver le micro',
  'voice.switchText': 'Passer au texte', 'voice.endCall': 'Terminer l’appel',
  'voice.subtitlesShow': 'Afficher les sous-titres', 'voice.subtitlesHide': 'Masquer les sous-titres',
  'voice.agentVoice': 'Voix de l’agent', 'voice.appliesNext': 'S’applique à la prochaine réponse.',
  'v2t.input': 'Saisie vocale', 'v2t.listening': 'À l’écoute…', 'v2t.transcribing': 'Transcription…',
  'v2t.review': 'Révision', 'v2t.microphone': 'Microphone',
  'v2t.transcribingBody': 'Transcription de votre audio…',
  'v2t.stopTranscribe': 'Arrêter et transcrire', 'v2t.placeholder': 'Modifiez la transcription avant d’envoyer…',
  'v2t.willAppear': 'Vos paroles apparaîtront ici pendant que vous parlez.',
  'v2t.listeningCount': 'À l’écoute… {{s}}s restantes. Vos paroles apparaîtront ici.',
  'v2t.errorMic': 'Accès au microphone refusé. Veuillez autoriser l’accès et réessayer.',
  'v2t.errorTranscribe': 'Échec de la transcription. Veuillez réessayer.',
  'common.cancel': 'Annuler', 'common.send': 'Envoyer', 'common.close': 'Fermer',
  'sug.anon.0': 'Quelles offres proposez-vous ?',
  'sug.anon.1': 'Vers quels pays livrez-vous ?',
  'sug.anon.2': 'Comment fonctionne l’essai gratuit ?',
  'sug.anon.3': 'Où puis-je lire votre politique de confidentialité ?',
  'sug.anon.4': 'Mettre à jour mon adresse de facturation',
  'sug.anon.5': 'Réinitialiser ma clé API',
  'sug.auth.0': 'Mettre à jour mon adresse de facturation',
  'sug.auth.1': 'Renouveler ma clé API de production',
  'sug.auth.2': 'Renvoyer la dernière facture en PDF',
  'sug.auth.3': 'Ajouter un collègue à mon espace de travail',
}

const ar: typeof en = {
  'greeting.morning': 'صباح الخير', 'greeting.afternoon': 'مساء الخير', 'greeting.evening': 'مساء الخير',
  'hero.help': 'كيف يمكنني مساعدتك؟',
  'hero.disclaimer': 'قد يرتكب {{org}} أخطاء. تحقّق من المعلومات المهمة.',
  'chat.placeholder': 'اكتب سؤالك…',
  'chat.placeholderAuth': 'اسأل عن حسابك أو طلباتك أو فواتيرك…',
  'chat.thinking': 'يفكّر',
  'chat.send': 'إرسال',
  'chat.online': 'مساعد {{org}} · متصل',
  'chat.signedOut': 'غير مسجّل الدخول',
  'chat.latest': 'الأحدث',
  'chat.sources': 'المصادر',
  'chat.helpful': 'مفيد', 'chat.notHelpful': 'غير مفيد', 'chat.copy': 'نسخ',
  'chat.footer': 'دعم عملاء {{org}}',
  'banner.anon': 'أنت تتحدّث بشكل مجهول.', 'banner.anonBody': 'الإجراءات المرتبطة بالحساب معطّلة.',
  'banner.signInUnlock': 'سجّل الدخول لتفعيلها',
  'sidebar.newConversation': 'محادثة جديدة',
  'sidebar.recent': 'الأخيرة', 'sidebar.signedOut': 'غير مسجّل', 'sidebar.syncing': 'جارٍ المزامنة…',
  'sidebar.noConversations': 'لا توجد محادثات بعد.',
  'sidebar.anonTitle': 'محادثة مجهولة',
  'sidebar.anonBody': 'تبقى محادثتك على هذا الجهاز.',
  'sidebar.signInTo': 'سجّل الدخول', 'sidebar.signInRest': 'لحفظ السجل وتفعيل إجراءات الحساب.',
  'sidebar.collapse': 'طيّ', 'sidebar.expand': 'توسيع', 'sidebar.menu': 'القائمة',
  'sidebar.untitled': 'محادثة بدون عنوان',
  'theme.light': 'فاتح', 'theme.dark': 'داكن',
  'auth.signIn': 'تسجيل الدخول', 'auth.signOut': 'تسجيل الخروج',
  'composer.attach': 'إرفاق ملف', 'composer.image': 'إدراج صورة', 'composer.voiceInput': 'إدخال صوتي',
  'composer.liveVoice': 'صوت مباشر', 'composer.liveVoiceCall': 'مكالمة صوتية مباشرة', 'composer.send': 'إرسال',
  'voice.connecting': 'جارٍ الاتصال…',
  'voice.listeningHint': 'أستمع — تحدّث متى شئت',
  'voice.thinking': 'جارٍ التفكير…',
  'voice.live': 'صوت مباشر', 'voice.transcript': 'النص المباشر',
  'voice.transcriptEmpty': 'ستظهر المحادثة هنا أثناء حديثك.',
  'voice.you': 'أنت', 'voice.mute': 'كتم', 'voice.unmute': 'إلغاء الكتم',
  'voice.switchText': 'التبديل إلى النص', 'voice.endCall': 'إنهاء المكالمة',
  'voice.subtitlesShow': 'إظهار الترجمة', 'voice.subtitlesHide': 'إخفاء الترجمة',
  'voice.agentVoice': 'صوت المساعد', 'voice.appliesNext': 'يُطبّق من الرد التالي.',
  'v2t.input': 'إدخال صوتي', 'v2t.listening': 'أستمع…', 'v2t.transcribing': 'جارٍ التفريغ…',
  'v2t.review': 'مراجعة', 'v2t.microphone': 'الميكروفون',
  'v2t.transcribingBody': 'جارٍ تفريغ الصوت…',
  'v2t.stopTranscribe': 'إيقاف وتفريغ', 'v2t.placeholder': 'عدّل النص قبل الإرسال…',
  'v2t.willAppear': 'ستظهر كلماتك هنا أثناء حديثك.',
  'v2t.listeningCount': 'أستمع… يتبقّى {{s}} ثانية. ستظهر كلماتك هنا.',
  'v2t.errorMic': 'تم رفض إذن الميكروفون. يرجى السماح بالوصول والمحاولة مرة أخرى.',
  'v2t.errorTranscribe': 'فشل التفريغ. يرجى المحاولة مرة أخرى.',
  'common.cancel': 'إلغاء', 'common.send': 'إرسال', 'common.close': 'إغلاق',
  'sug.anon.0': 'ما الخطط التي تقدّمونها؟',
  'sug.anon.1': 'إلى أي دول تشحنون؟',
  'sug.anon.2': 'كيف تعمل التجربة المجانية؟',
  'sug.anon.3': 'أين أقرأ سياسة الخصوصية؟',
  'sug.anon.4': 'تحديث عنوان الفوترة',
  'sug.anon.5': 'إعادة تعيين مفتاح API',
  'sug.auth.0': 'تحديث عنوان الفوترة',
  'sug.auth.1': 'تدوير مفتاح API للإنتاج',
  'sug.auth.2': 'إعادة إرسال آخر فاتورة بصيغة PDF',
  'sug.auth.3': 'إضافة زميل إلى مساحة العمل',
}

const resources = {
  en: { translation: en },
  fr: { translation: fr },
  ar: { translation: ar },
}

export const SUPPORTED_LOCALES = ['en', 'fr', 'ar'] as const
export const LOCALE_LABELS: Record<string, string> = { en: 'EN', fr: 'FR', ar: 'ع' }
export const LOCALE_NAMES: Record<string, string> = { en: 'English', fr: 'Français', ar: 'العربية' }
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur'])

export function isRtl(lng: string): boolean {
  return RTL_LOCALES.has((lng || 'en').split('-')[0])
}
export function dirFor(lng: string): 'rtl' | 'ltr' {
  return isRtl(lng) ? 'rtl' : 'ltr'
}

/** Minimum letters before a message is allowed to switch the whole UI. */
const MIN_LETTERS_TO_SWITCH = 8

/** Share of letters that must be Arabic before we treat the text as Arabic. */
const ARABIC_DOMINANCE = 0.5

const ARABIC_LETTER = /[؀-ۿ]/g
const LATIN_LETTER = /[a-zà-öø-ÿ]/gi
const FRENCH_HINT = /[àâçéèêëîïôûùüÿœ]|\b(je|vous|nous|est|c'est|bonjour|merci|pourquoi|comment|s'il)\b/gi

/**
 * Cheap script/stopword language detection for auto-switching the UI.
 *
 * Switching the UI locale also flips the entire layout to RTL, so this has to
 * be conservative. It previously returned 'ar' if the text contained a SINGLE
 * Arabic character anywhere — so one pasted name, product code or quoted word
 * inside an otherwise English sentence flipped the whole interface to Arabic
 * right-to-left, and typing in English again flipped it back. Detection now
 * requires the script to actually dominate the message, and ignores messages
 * too short to carry real evidence.
 *
 * Returns null when the text is inconclusive, meaning "leave the UI alone".
 */
export function detectLang(text: string): string | null {
  const arabic = (text.match(ARABIC_LETTER) || []).length
  const latin = (text.match(LATIN_LETTER) || []).length
  const letters = arabic + latin

  if (letters < MIN_LETTERS_TO_SWITCH) return null

  if (arabic / letters >= ARABIC_DOMINANCE) return 'ar'
  // A couple of French markers beat plain English; one is too weak, since
  // "comment" and loan words like "merci" show up in English text too.
  if ((text.match(FRENCH_HINT) || []).length >= 2) return 'fr'
  if (latin > 0) return 'en'
  return null
}

export async function initI18n(): Promise<void> {
  const lng = detectLocale()
  applyRtl(lng)
  await i18n.use(initReactI18next).init({
    resources, lng, fallbackLng: 'en',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  })
}

export function changeLocale(lng: string, manual = true): Promise<void> {
  if (!SUPPORTED_LOCALES.includes(lng as typeof SUPPORTED_LOCALES[number])) return Promise.resolve()
  if (manual) localStorage.setItem('helix_locale', lng)
  applyRtl(lng)
  return i18n.changeLanguage(lng).then(() => undefined)
}

/** True once the customer has explicitly chosen a language (stops auto-switch). */
export function hasManualLocale(): boolean {
  return localStorage.getItem('helix_locale') != null
}

function applyRtl(lng: string): void {
  const rtl = isRtl(lng)
  document.documentElement.dir = rtl ? 'rtl' : 'ltr'
  document.documentElement.lang = lng
}

function detectLocale(): string {
  const fromStorage = localStorage.getItem('helix_locale')
  if (fromStorage && SUPPORTED_LOCALES.includes(fromStorage as typeof SUPPORTED_LOCALES[number])) return fromStorage
  const nav = (navigator.language || 'en').split('-')[0]
  return SUPPORTED_LOCALES.includes(nav as typeof SUPPORTED_LOCALES[number]) ? nav : 'en'
}

export default i18n
